/**
 * THE address field group. Used by S6 (order details) and My Account (profile
 * default). There is deliberately no second address form: the ZIP handling, the
 * region handling and the canonical write shape all live here, so the two
 * surfaces cannot drift.
 *
 * FIELD ORDER — ZIP LAST. We do not look addresses up from a ZIP, so leading
 * with it implied a lookup that never happens. The courtesy fill of a blank
 * city/state from the local table remains (it never gates anything and never
 * overwrites anything typed), but it is now the last thing that can happen
 * rather than the first thing asked for.
 */
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Field, SectionTitle, inputClass, TickIcon } from "@/components/consumer/chrome";
import {
  AddressDraft,
  ZIP_AUTOFILL,
  isZipFormat,
  US_STATES,
  SUPPORTED_COUNTRIES,
  countryLabel,
  normaliseCountry,
} from "@/lib/postal-address";

export interface AddressFieldLabels {
  title: string;
  hint: string;
  zipLabel: string;
  zipPlaceholder: string;
  zipError: string;
  change: string;
  street: string;
  streetPlaceholder: string;
  unit: string;
  optionalBadge: string;
  unitPlaceholder: string;
  city: string;
  state: string;
  statePlaceholder: string;
  country: string;
  usOnly: string;
}

export const DEFAULT_ADDRESS_LABELS: AddressFieldLabels = {
  title: "Delivery address",
  hint: "",
  zipLabel: "ZIP code",
  zipPlaceholder: "10001",
  zipError: "ZIP codes are 5 digits",
  change: "Change",
  street: "Street address",
  streetPlaceholder: "123 Main St",
  unit: "Apartment, unit, floor",
  optionalBadge: "Optional",
  unitPlaceholder: "Apt 4B",
  city: "City",
  state: "State",
  statePlaceholder: "Select state",
  country: "Country",
  usOnly: "US addresses only for now.",
};

/** The chevron that marks the state control as a dropdown. */
function SelectChevron() {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden="true"
      className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/45"
    >
      <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface Props {
  value: AddressDraft;
  onChange: (patch: Partial<AddressDraft>) => void;
  labels?: Partial<AddressFieldLabels>;
  /** Hide the section heading when the host screen supplies its own. */
  showTitle?: boolean;
  /** Consumer checkout is US-only, so its address screen does not show a fixed field. */
  showCountry?: boolean;
  testId?: string;
}

export default function AddressFields({
  value,
  onChange,
  labels,
  showTitle = true,
  showCountry = true,
  testId = "address-fields",
}: Props) {
  const L = { ...DEFAULT_ADDRESS_LABELS, ...(labels ?? {}) };
  // Presentational only: did the local table just fill city/state for them?
  // NOT a validity flag and NOT a submission precondition.
  const [autofilled, setAutofilled] = useState(false);

  /**
   * ZIP entry NEVER fails and never gates anything. A table hit fills a BLANK
   * city or state as a courtesy; a miss just leaves them as typed.
   */
  function onZipInput(v: string) {
    const digits = v.replace(/\D/g, "").slice(0, 5);
    const patch: Partial<AddressDraft> = { postcode: digits };
    if (digits.length === 5) {
      const hit = ZIP_AUTOFILL[digits];
      if (hit) {
        if (!value.city.trim()) patch.city = hit[0];
        if (!value.region.trim()) patch.region = hit[1];
        onChange(patch);
        setAutofilled(!value.city.trim() || !value.region.trim());
        return;
      }
    }
    onChange(patch);
    setAutofilled(false);
  }

  return (
    <div data-testid={testId}>
      {showTitle ? (
        <>
          <SectionTitle className="mb-3.5">{L.title}</SectionTitle>
          {L.hint ? (
            <p className="mb-3.5 text-[14px] leading-[1.5] text-foreground/60">{L.hint}</p>
          ) : null}
        </>
      ) : null}

      <div className="flex flex-col gap-3.5">
        <Field label={L.street}>
          <input
            value={value.line1}
            onChange={(e) => onChange({ line1: e.target.value })}
            autoComplete="address-line1"
            placeholder={L.streetPlaceholder}
            className={inputClass}
            data-testid={`${testId}-line1`}
          />
        </Field>
        <Field
          label={
            (
              <span className="flex items-center gap-2">
                {L.unit}
                <span className="rounded-full bg-foreground/[0.07] px-2 py-[3px] text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-muted">
                  {L.optionalBadge}
                </span>
              </span>
            ) as unknown as string
          }
        >
          <input
            value={value.line2}
            onChange={(e) => onChange({ line2: e.target.value })}
            autoComplete="address-line2"
            placeholder={L.unitPlaceholder}
            className={inputClass}
            data-testid={`${testId}-line2`}
          />
        </Field>
        <div className="flex gap-2">
          <Field label={L.city} className="min-w-0 flex-1">
            <input
              value={value.city}
              onChange={(e) => onChange({ city: e.target.value })}
              autoComplete="address-level2"
              className={inputClass}
              data-testid={`${testId}-city`}
            />
          </Field>
          {/* SELECT, not free text: the server CHECKs `^[A-Z]{2}$`, so "Texas"
              and "tx" were rejections waiting to happen. */}
          <Field label={L.state} className="w-[132px] flex-none">
            <div className="relative">
              <select
                value={value.region}
                onChange={(e) => onChange({ region: e.target.value })}
                autoComplete="address-level1"
                className={cn(inputClass, "appearance-none pl-2.5 pr-8")}
                data-testid={`${testId}-region`}
              >
                <option value="">{L.statePlaceholder}</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code} — {s.label}
                  </option>
                ))}
              </select>
              <SelectChevron />
            </div>
          </Field>
        </div>

        {/* COUNTRY. One delivery market today (US), so this is a fixed value
            rather than a one-option dropdown; add a market to
            SUPPORTED_COUNTRIES and it becomes a real select with no change
            here. Either way the value leaving this form is alpha-2. */}
        {showCountry ? <Field label={L.country}>
          {SUPPORTED_COUNTRIES.length > 1 ? (
            <div className="relative">
              <select
                value={normaliseCountry(value.country) || "US"}
                onChange={(e) => onChange({ country: e.target.value })}
                autoComplete="country"
                className={cn(inputClass, "appearance-none pr-8")}
                data-testid={`${testId}-country`}
              >
                {SUPPORTED_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
              <SelectChevron />
            </div>
          ) : (
            <div
              className="flex min-h-[52px] items-center justify-between rounded-xl bg-foreground/[0.05] px-[15px] text-[16px] font-medium text-foreground/75"
              data-testid={`${testId}-country`}
              data-country={SUPPORTED_COUNTRIES[0].code}
            >
              <span>{countryLabel(SUPPORTED_COUNTRIES[0].code)}</span>
              <span className="text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-muted">
                {SUPPORTED_COUNTRIES[0].code}
              </span>
            </div>
          )}
        </Field> : null}

        {/* ZIP LAST — see the note at the top of this file. */}
        <Field
          label={L.zipLabel}
          // FORMAT feedback only, and only once they have typed something
          // partial. Never "we don't recognise that ZIP".
          error={value.postcode.length > 0 && !isZipFormat(value.postcode) ? L.zipError : undefined}
        >
          <input
            value={value.postcode}
            onChange={(e) => onZipInput(e.target.value)}
            inputMode="numeric"
            maxLength={5}
            autoComplete="postal-code"
            placeholder={L.zipPlaceholder}
            className={cn(inputClass, "tracking-[0.06em]")}
            data-testid={`${testId}-zip`}
          />
        </Field>

        {autofilled && (
          <div className="flex items-center gap-2.5 rounded-xl bg-leaf/10 px-[15px] py-3">
            <TickIcon />
            <span className="text-[15px] font-semibold text-[#1A6B42]">
              {value.city}, {value.region}
            </span>
            <span className="flex-1" />
            <button
              type="button"
              onClick={() => setAutofilled(false)}
              className="min-h-11 px-1 py-2.5 text-[14px] font-semibold text-[#1A6B42]"
            >
              {L.change}
            </button>
          </div>
        )}

        <p className="text-[13px] leading-[1.5] text-foreground/55">{L.usOnly}</p>
      </div>
    </div>
  );
}
